/**
 * A cadeia de foco — o `EnterProximoCampo` do sistema de hoje, feito direito.
 *
 * No legado a ordem é uma corrente de `if` escrita à mão:
 * `arquivo → circuito → chicote → cor → restricao → obs → componente →
 *  conector → pino → tipo → botão`. Funciona, mas qualquer campo novo obriga a
 * mexer na corrente inteira, e ninguém sabe a ordem sem ler o JavaScript.
 *
 * Aqui a ordem é **declarada**: cada campo se registra com um nome, e andar é
 * pedir o próximo da lista. Acrescentar campo é acrescentar uma linha na ordem.
 *
 * Por que isso importa: *"tem horas que eles fazem duzentas inserções
 * simultâneas"*. Cada vez que a mão sai do teclado para o mouse, some meio
 * segundo — em duzentas linhas, isso é o dia inteiro.
 */

import { useCallback, useMemo, useRef } from "react"

export type CadeiaDeFoco = {
  /** Guarda o elemento daquele passo. Use como `ref={cadeia.registrar("cor")}`. */
  registrar: (nome: string) => (el: HTMLElement | null) => void
  /** Move para o passo seguinte ao nome dado. */
  andar: (de: string) => void
  /** Move para um passo específico — o "voltar ao primeiro campo" depois de gravar. */
  ir: (nome: string) => void
}

export function usarCadeiaDeFoco(ordem: string[]): CadeiaDeFoco {
  const campos = useRef(new Map<string, HTMLElement>())

  const registrar = useCallback((nome: string) => (el: HTMLElement | null) => {
    if (el) campos.current.set(nome, el)
    else campos.current.delete(nome)
  }, [])

  const focar = useCallback((nome: string) => {
    const el = campos.current.get(nome)
    if (!el) return false
    // O elemento registrado pode ser o invólucro; o que recebe foco é o input.
    const alvo = el instanceof HTMLInputElement || el instanceof HTMLSelectElement
      ? el
      : el.querySelector<HTMLElement>("input, select, textarea, button")
    if (!alvo) return false
    alvo.focus()
    if (alvo instanceof HTMLInputElement) alvo.select()
    return true
  }, [])

  const andar = useCallback((de: string) => {
    const i = ordem.indexOf(de)
    if (i < 0) return
    // Pula o que não está montado — um campo escondido não deve engolir o foco.
    for (let n = i + 1; n < ordem.length; n++) {
      if (focar(ordem[n]!)) return
    }
  }, [ordem, focar])

  const ir = useCallback((nome: string) => { focar(nome) }, [focar])

  // Mesma identidade enquanto a ordem não muda: quem põe a cadeia em lista de
  // dependência de efeito não deve vê-lo rodar a cada render.
  return useMemo(() => ({ registrar, andar, ir }), [registrar, andar, ir])
}
